import { AlertTriangle, Ban, Building2, Calendar, CheckCircle2, CircleSlash, Eye, Network, ShieldCheck, Star, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type {
  Papel,
  Saude,
  StatusAgendamento,
  StatusContrato,
  StatusHistorico,
} from '@/data/types';

/**
 * Hard rule 2 — estado nunca é só cor.
 *
 * Cada função abaixo devolve cor + ícone + rótulo textual. É a substituição
 * direta das `chip-*` por `styleByValue` que o app original declarava dentro de
 * cada layout JSON: a mesma semântica passa a viver num lugar só, tipada, e o
 * daltônico lê o glifo e o texto mesmo sem distinguir o matiz.
 */

export function SaudeBadge({ valor }: { valor: Saude | null }) {
  if (valor === 'OK') return <Badge tone="success" icon={<CheckCircle2 aria-hidden="true" />}>Saudável</Badge>;
  if (valor === 'Atencao') return <Badge tone="warning" icon={<AlertTriangle aria-hidden="true" />}>Atenção</Badge>;
  if (valor === 'Incidente') return <Badge tone="danger" icon={<XCircle aria-hidden="true" />}>Incidente</Badge>;
  return <Badge tone="pending">Sem dado</Badge>;
}

export function PapelBadge({ valor }: { valor: Papel | null }) {
  if (valor === 'Controladora') return <Badge tone="primary" icon={<Network aria-hidden="true" />}>Controladora</Badge>;
  if (valor === 'Controlada') return <Badge tone="info" icon={<Network aria-hidden="true" />}>Controlada</Badge>;
  if (valor === 'Instalação') return <Badge tone="neutral">Instalação</Badge>;
  return <Badge tone="pending">Sem dado</Badge>;
}

/**
 * Flags de cadastro da instalação: ANA (cliente ANA) e Interno (cliente da
 * própria Nasajon). Cada uma só aparece quando verdadeira — a ausência é o
 * caso comum e não precisa de badge. Usado em todas as listagens.
 */
export function InstalacaoFlags({ ana, interno }: { ana: boolean; interno: boolean }) {
  if (!ana && !interno) return null;
  // Espaçamento próprio (não depende do `gap` de quem chama) e `shrink-0`
  // pra os badges nunca se espremerem contra o nome nem entre si.
  return (
    <span className="inline-flex shrink-0 items-center gap-2">
      {ana ? (
        <Badge tone="warning" className="px-2.5" icon={<Star aria-hidden="true" />}>
          ANA
        </Badge>
      ) : null}
      {interno ? (
        <Badge tone="info" className="px-2.5" icon={<Building2 aria-hidden="true" />}>
          Interno
        </Badge>
      ) : null}
    </span>
  );
}

/** Administrador do tenant (Diretório, com cascata Controladora → Controladas) vs. acesso só leitura. */
export function AdministradorBadge({ valor }: { valor: boolean }) {
  if (valor) return <Badge tone="primary" icon={<ShieldCheck aria-hidden="true" />}>Administrador</Badge>;
  return <Badge tone="neutral" icon={<Eye aria-hidden="true" />}>Somente leitura</Badge>;
}

export function StatusAgendamentoBadge({ valor }: { valor: StatusAgendamento }) {
  switch (valor) {
    case 'Programado':
      return <Badge tone="success" icon={<Calendar aria-hidden="true" />}>Programado</Badge>;
    case 'Executando':
      return <Badge tone="info" icon={<Network aria-hidden="true" />}>Executando</Badge>;
    case 'Concluído':
      return <Badge tone="success" filled icon={<CheckCircle2 aria-hidden="true" />}>Concluído</Badge>;
    case 'Falha':
      return <Badge tone="danger" icon={<XCircle aria-hidden="true" />}>Falhou</Badge>;
    case 'Falha Parcial':
      return <Badge tone="warning" icon={<AlertTriangle aria-hidden="true" />}>Falha parcial</Badge>;
    case 'Cancelado':
    default:
      return <Badge tone="danger" icon={<Ban aria-hidden="true" />}>Cancelado</Badge>;
  }
}

export function StatusHistoricoBadge({ valor }: { valor: StatusHistorico }) {
  switch (valor) {
    case 'Concluído':
      return <Badge tone="success" filled icon={<CheckCircle2 aria-hidden="true" />}>Concluído</Badge>;
    case 'Falha':
      return <Badge tone="danger" icon={<XCircle aria-hidden="true" />}>Falhou</Badge>;
    case 'Falha Parcial':
      return <Badge tone="warning" icon={<AlertTriangle aria-hidden="true" />}>Falha parcial</Badge>;
    case 'Programado':
      return <Badge tone="info" icon={<Calendar aria-hidden="true" />}>Programado</Badge>;
    case 'Cancelado':
      return <Badge tone="neutral" icon={<Ban aria-hidden="true" />}>Cancelado</Badge>;
    default:
      return <Badge tone="pending">Sem dado</Badge>;
  }
}

export function StatusContratoBadge({ valor }: { valor: StatusContrato | null }) {
  if (valor === 'Ativo') return <Badge tone="success" icon={<CheckCircle2 aria-hidden="true" />}>Contrato ativo</Badge>;
  if (valor === 'Suspenso') return <Badge tone="warning" icon={<AlertTriangle aria-hidden="true" />}>Contrato suspenso</Badge>;
  if (valor === 'Inativo') return <Badge tone="danger" icon={<CircleSlash aria-hidden="true" />}>Contrato inativo</Badge>;
  return <Badge tone="pending">Sem dado</Badge>;
}
